import { useState, useEffect, useRef } from 'react';
import { fetchCached } from '@/lib/api';
import SectionWatermark from '@/components/SectionWatermark';
import { Heart, Users, ShieldCheck, Gem, Eye, Handshake, HandHelping } from 'lucide-react';
import { FALLBACK_ABOUT_CONTENT } from '@/lib/publicPageData';

const ICON_MAP = {
  'Love': Heart, 'Compassion': HandHelping, 'Fellowship': Users,
  'Integrity': ShieldCheck, 'Respect': Gem, 'Honesty': Eye, 'Teamwork': Handshake,
};

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
}

function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div ref={ref} className={className}
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(24px)', transition: `all 0.7s ease-out ${delay}ms` }}>
      {children}
    </div>
  );
}

export default function AboutPage() {
  const [content, setContent] = useState(FALLBACK_ABOUT_CONTENT);

  useEffect(() => {
    fetchCached('/content/about').then((data) => {
      setContent({
        ...FALLBACK_ABOUT_CONTENT,
        ...(data && typeof data === 'object' ? data : {}),
        values: data?.values?.length ? data.values : FALLBACK_ABOUT_CONTENT.values,
      });
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page" className="pt-20 mscm-public-shell">
      {/* Hero */}
      <section className="mscm-hero-panel mscm-cinematic-hero py-20 md:py-28">
        <div className="mscm-hero-aurora" />
        <div className="mscm-star-field" />
        <div className="mscm-luminous-sweep" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <span className="text-white/85 text-xs font-bold uppercase tracking-[0.16em]">Million Stars Community Ministry</span>
          <h1 className="mscm-hero-title mt-4 font-bold text-white lg:text-6xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            About Us
          </h1>
          <p className="mt-4 text-white/80 text-base md:text-lg max-w-2xl mx-auto">
            Learn about our mission, vision, and the values that guide everything we do.
          </p>
        </div>
      </section>

      {/* Mission */}
      <SectionWatermark
        className="mscm-section mscm-section-orbit mscm-diagonal-section"
        contentClassName="max-w-7xl mx-auto px-6 md:px-8"
        watermarkClassName="mscm-watermark-strong mscm-watermark-mission"
        opacity={0.24}
      >
          <div className="mscm-section-intro mb-14">
            <span className="mscm-eyebrow">Purpose In Motion</span>
            <h2 className="mscm-section-title mscm-display mt-3 font-bold md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              The heart behind the ministry
            </h2>
          </div>
          <div className="mscm-mission-layout grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            <AnimatedSection>
              <div className="mscm-mission-statement mscm-mission-balanced h-full">
                <div className="mb-6 flex items-center justify-between gap-4">
                  <div className="mscm-mission-symbol">
                    <Heart size={22} />
                    <span>Mission</span>
                  </div>
                  <span className="mscm-mission-number">01</span>
                </div>
                <h2 data-testid="mission-title" className="mscm-display text-3xl md:text-4xl font-bold leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {content.mission_title}
                </h2>
                <p className="mt-6 text-[#26364A] text-base leading-relaxed">
                  {content.mission_text}
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={150}>
              <div className="mscm-mission-statement mscm-mission-balanced h-full">
                <div className="mb-6 flex items-center justify-between gap-4">
                  <div className="mscm-mission-symbol">
                    <Eye size={22} />
                    <span>Vision</span>
                  </div>
                  <span className="mscm-mission-number">02</span>
                </div>
                <h2 data-testid="vision-title" className="mscm-display text-3xl md:text-4xl font-bold leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {content.vision_title}
                </h2>
                <p className="mt-6 text-[#26364A] text-base leading-relaxed">
                  {content.vision_text}
                </p>
              </div>
            </AnimatedSection>
          </div>
      </SectionWatermark>

      {/* Core Values */}
      <section data-testid="about-values-section" className="mscm-section mscm-section-soft">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="mscm-eyebrow">Values</span>
              <h2 className="mscm-display mt-3 text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {content.values_title}
              </h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(content.values || []).map((value, i) => {
              const IconComp = ICON_MAP[value.title] || Heart;
              return (
                <AnimatedSection key={i} delay={i * 80}>
                  <div className="mscm-value-item p-6 md:p-7 h-full">
                    <div className="w-14 h-14 bg-[#EAF4FF]/70 rounded-[8px] flex items-center justify-center mb-5">
                      <IconComp size={26} className="text-[#1E4FA1]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0F172A] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {value.title}
                    </h3>
                    <p className="text-[#475569] text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
